import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Box } from '@pets-ui/Box/Box'
import { ButtonBase } from '@pets-ui/ButtonBase/ButtonBase'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowLeftLarge, ArrowRightLarge } from '@pets-ui/icons'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useState, type ComponentPropsWithoutRef, type PropsWithChildren } from 'react'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  hideScrollBar: {
    'scrollbarWidth': 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  arrowButton: {
    borderColor: colors.grey200,
    borderRadius: '50%',
    borderStyle: 'solid',
    borderWidth: '1px',
    alignItems: 'center',
    backgroundColor: colors.white,
    boxShadow: '0px 2px 4px 0 rgba(0, 0, 0, 0.08)',
    color: colors.grey600,
    display: {
      [MOBILE]: 'none',
      default: 'flex',
    },
    justifyContent: 'center',
    opacity: 0,
    pointerEvents: 'none',
    height: 38,
    width: 38,
  },
  arrowLeft: {
    position: 'absolute',
    transform: 'translate(0, -100%)',
    zIndex: 2,
    left: 0,
    top: '50%',
  },
  arrowRight: {
    position: 'absolute',
    transform: 'translate(0, -100%)',
    zIndex: 2,
    right: 0,
    top: '50%',
  },
  fadeIn: {
    opacity: 1,
    pointerEvents: 'auto',
  },
})

export type CartSpecialOfferProductListProps = ComponentPropsWithoutRef<'div'>

export const CartSpecialOfferProductList = (props: PropsWithChildren<CartSpecialOfferProductListProps>) => {
  const { children, ...rest } = props
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null)
  const [showArrows, setShowArrows] = useState<{
    left: boolean
    right: boolean
  }>({
    left: false,
    right: false,
  })

  const updateArrowsDisplay = useCallbackEvent(() => {
    if (containerElement) {
      setShowArrows({
        left: containerElement.scrollLeft > 0,
        right: containerElement.scrollLeft < containerElement.scrollWidth - containerElement.offsetWidth,
      })
    }
  })

  useEffect(() => {
    containerElement?.addEventListener('scroll', updateArrowsDisplay)
    // 初始化時就要觸發一次，確保一開始就有正確的箭頭顯示
    updateArrowsDisplay()

    return () => {
      containerElement?.removeEventListener('scroll', updateArrowsDisplay)
    }
    // deps 必須有 children 確保容器長度有變動時重新拿 scrollWidth 計算，否則只有 containerElement 一開始初始化完後就不會再變動
  }, [updateArrowsDisplay, containerElement, children])

  const handleLeftClick = () => {
    if (containerElement) {
      const minLeft = Math.max(0, containerElement.scrollLeft - containerElement.offsetWidth)
      containerElement.scrollTo({
        left: minLeft,
        behavior: 'smooth',
      })
    }
  }

  const handleRightClick = () => {
    if (containerElement) {
      const maxLeft = Math.min(containerElement.scrollLeft + containerElement.offsetWidth, containerElement.scrollWidth - containerElement.offsetWidth)
      containerElement.scrollTo({
        left: maxLeft,
        behavior: 'smooth',
      })
    }
  }

  return (
    <Box position="relative" {...rest}>
      <ButtonBase
        rootStyle={[styles.arrowButton, styles.arrowLeft, showArrows.left && styles.fadeIn]}
        onClick={handleLeftClick}
      >
        <ArrowLeftLarge fontSize={18} />
      </ButtonBase>
      <HStack rootRef={setContainerElement} gap={3} flexWrap="nowrap" overflowX="auto" alignItems="flex-start" rootStyle={styles.hideScrollBar}>
        {children}
      </HStack>
      <ButtonBase
        rootStyle={[styles.arrowButton, styles.arrowRight, showArrows.right && styles.fadeIn]}
        onClick={handleRightClick}
      >
        <ArrowRightLarge fontSize={18} />
      </ButtonBase>
    </Box>
  )
}
